import { createContext, type ReactNode, useContext, useEffect, useState } from "react";

export const USER_LOCALE_KEY = "user_locale/v1";

type LocaleProviderProps<T extends readonly string[]> = {
	children: ReactNode;
	supportedLocales: T;
	defaultLocale: T[number];
};

type LocaleContextType<T extends readonly string[] = readonly string[]> = {
	locale: T[number];
	setLocale: (locale: T[number]) => void;
	supportedLocales: T;
};

const LocaleContext = createContext<LocaleContextType<any> | undefined>(undefined);

export const Provider = <T extends readonly string[]>({
	children,
	supportedLocales,
	defaultLocale,
}: LocaleProviderProps<T>) => {
	const [locale, setLocaleState] = useState<T[number]>(() => {
		// Get locale from localStorage on initialization
		if (typeof window !== "undefined") {
			const storedLocale = window.localStorage.getItem(USER_LOCALE_KEY);
			if (storedLocale && supportedLocales.includes(storedLocale)) {
				return storedLocale;
			}

			// Fallback to browser language if supported
			const browserLocale = navigator.language;
			if (supportedLocales.includes(browserLocale)) {
				return browserLocale;
			}
		}

		return defaultLocale;
	});

	const setLocale = (newLocale: T[number]) => {
		if (!supportedLocales.includes(newLocale)) {
			console.warn(`Locale "${newLocale}" is not supported. Supported locales: ${supportedLocales.join(", ")}`);
			return;
		}

		setLocaleState(newLocale);

		// Store in localStorage
		if (typeof window !== "undefined") {
			window.localStorage.setItem(USER_LOCALE_KEY, newLocale);
		}
	};

	// Update document language attribute when locale changes
	useEffect(() => {
		if (typeof document !== "undefined") {
			document.documentElement.lang = locale;
		}
	}, [locale]);

	const value: LocaleContextType<T> = {
		locale,
		setLocale,
		supportedLocales,
	};

	return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
};

export const useLocale = <T extends readonly string[] = readonly string[]>() => {
	const context = useContext(LocaleContext) as LocaleContextType<T> | undefined;
	if (!context) {
		throw new Error("useLocale must be used within a LocaleProvider");
	}
	return context;
};

// Helper hook for getting current locale only
export const useCurrentLocale = () => {
	const { locale } = useLocale();
	return locale;
};
